import type { ReactNode } from 'react'
import type { ChangelogEntry } from './componentsV2/RevisionHistory/RevisionHistorySection'

export enum MedicationStockReason {
  INITIAL_STOCK = 'INITIAL_STOCK',
  RESTOCK = 'RESTOCK',
  MED_ADMIN = 'MED_ADMIN',
  DISPOSAL = 'DISPOSAL',
  STOCK_COUNT = 'STOCK_COUNT',
  DISCONTINUED = 'DISCONTINUED',
  NOT_ADMINISTERED = 'NOT_ADMINISTERED',
  RECONCILIATION = 'RECONCILIATION'
}

export enum DiscrepancyStatus {
  PENDING = 'PENDING',
  RESOLVED = 'Resolved',
  INVESTIGATING = 'INVESTIGATING',
  OPEN = 'Open'
}

export enum AdministrationStatus {
  SCHEDULED = 'SCHEDULED',
  GIVEN = 'GIVEN',
  MISSED = 'MISSED',
  REFUSED = 'REFUSED',
  OVERDUE = 'OVERDUE',
  PRN_AVAILABLE = 'PRN_AVAILABLE',
  OTHER = 'OTHER',
  ABSENT = 'ABSENT',
  VERBAL_PROMPT = 'VERBAL_PROMPT',
  ALREADY_TAKEN = 'ALREADY_TAKEN',
  LEFT_OUT = 'LEFT_OUT',
  REFUSED_DESTROYED = 'REFUSED_DESTROYED',
  NAUSEA_VOMITING = 'NAUSEA_VOMITING',
  NOT_NEEDED = 'NOT_NEEDED',
  PAUSED = 'PAUSED',
  DISCONTINUED = 'DISCONTINUED',
  UNKNOWN = 'UNKNOWN'
}

export type MedicationAction = {
  code?: string
  icon?: ReactNode
  translationKey: string
}

export type DisposalData = {
  description: string
  disposedStock: number
  newStock: number
  hasDiscrepancy: boolean
  discrepancyReason: string
  actionedBy: string
  actionTime?: any // dayjs object
  disposalMethod: string
  additionalDetails: boolean
  media?: File[]
  mediaAttachmentKeysToRemove?: string[]
}

export type StockCheckData = {
  description: string
  stockCount: number
  adjustment: number
  additionalDetails: boolean
  isExpectedStockAccurate: boolean
  resolveDiscrepancy?: string | null
  discrepancyReason?: string
  media?: File[]
  mediaAttachmentKeysToRemove?: string[]
}

export type MediaAttachment = {
  url: string
  key: string
  bucket: string
  contentType: string
  size: number
  uploadedAt: string
  uploadedBy: string
  _id?: string
}

export interface StockRecordWitness {
  userId: string
  witnessedAt: string
  /**
   * Set on an administration's witnesses, which the record drawer renders. The
   * API sends either a formatted string or the name object.
   */
  name?: { first: string; last: string } | string
  picture?: string | null
}

export interface StockRecordDiscrepancy {
  amount: number
  status: DiscrepancyStatus
  carerReason?: string
  reason?: string
}

export interface StockRecordListItem {
  id: string
  date: string
  recordedBy: string
  action: MedicationStockReason
  notes?: string | string[]
  expectedStock: number
  stockCount: number
  quantityChange: number
  unit: string
  witnessed: boolean
  witnesses?: StockRecordWitness[]
  discrepancy?: StockRecordDiscrepancy
}

export type ResidentMedication = {
  patternId: string
  residentMedicationId: string
  medicationName: string
  dose: string
  route: string
  /** Absent for a medication with stock tracking off. */
  currentStockLevel?: number
  unit: string
  stockAlertLevel?: number
  /** Computed by the API; when absent the UI compares stock itself. */
  isBelowThreshold?: boolean
  lastStockRecordDate: string
  lastStockCountDate: string
  daysSinceLastStockCount: Record<string, any>
  controlledDrug: boolean
  secondSignatureRequired: boolean
  discontinued: Record<string, any>
  status: string
  createdAt?: string
  updatedAt?: string
  createdByName?: { first: string; last: string }
  updatedByName?: { first: string; last: string }
  lastStockRecordedByName?: { first: string; last: string }
  trackStock?: boolean
}

export type StockRecord = {
  altDose?: string
  id: string
  date: string
  actionTime?: string
  stock?: { unit: string }
  administrationStatus?: AdministrationStatus
  type?: string
  eventType?: string
  recordedBy: {
    id: string
    name: {
      first: string
      last: string
    }
    picture?: string
  }
  receivedBy?: {
    id: string
    name: {
      first: string
      last: string
    }
    picture?: string
  }
  receivedFrom?: string
  action: MedicationStockReason
  notes?: string | string[]
  expectedStock?: number
  stockCount?: number
  quantityChange: number
  unit: string
  quantityType?: string
  witnessed: boolean
  witnesses?: StockRecordWitness[]
  hasDiscrepancy?: boolean
  discrepancy?: StockRecordDiscrepancy
  resident?: {
    id: string
    name: {
      first: string
      last: string
    }
    preferredName?: string
    picture?: string
  }
  disposedBy?: {
    id: string
    name: {
      first: string
      last: string
    }
  }
  disposalMethod?: string
  resultingStockLevel?: number
  previousStockLevel?: number
  mediaAttachments?: MediaAttachment[]
  changelog?: ChangelogEntry[]
  roundTime?: string
  backsignedAt?: string
  reasonForLateSignature?: string
  administeredBy?: {
    id: string
    name: { first: string; last: string } | string
    picture?: string | null
  }
  administration?: {
    quantityType?: string
    _id: string
    createdAt: string
    updatedAt: string
    recordedBy: {
      id: string
      name: {
        first: string
        last: string
      }
      picture?: string
    }
    updatedBy: string
    homeId: string
    status: string
    residentId: string
    patternId: string
    medicationId: string
    residentMedicationId: string
    roundTimingId: string
    startTime: string
    endTime: string
    dose: string
    customDose: string
    doseQuantity: number
    prn: boolean
    administrationStatus: AdministrationStatus | string
    administeredTime: string
    reasonCode?: string | null
    note?: string | null
    deleted: boolean
    emarRoundKey: string
    roundOverdue: boolean
    roundType: string
    resident?: {
      id: string
      name: {
        first: string
        last: string
      }
      firstName?: string
      picture?: string
    }
    createdByUser?: {
      id: string
      name: {
        first: string
        last: string
      }
      email?: string
      picture?: string
      [key: string]: any
    }
    [key: string]: any
  }
  stockChanged?: boolean | string
}

export type DiscrepancyRecord = {
  _id: string
  date: string
  actionTime: string
  actionedDate?: string
  residentMedicationId?: string
  resident: {
    id: string
    name: {
      first: string
      last: string
    }
    preferredName?: string
    picture?: string
  }
  medicationName: string
  prn: boolean
  controlledDrug: boolean
  resultingStockLevel: number
  previousStockLevel: number
  quantityChange: number
  unit: string
  reason: string
  recordedBy: {
    id: string
    name: {
      first: string
      last: string
    }
    picture?: string | null
  }
  recordedByUser?: {
    id: string
    name: {
      first: string
      last: string
    }
    email?: string
    picture?: string
    [key: string]: any
  }
  witnesses?: any[]
  notes?: string | string[]
  systemDescription?: string
  discrepancy: {
    amount: number
    status: DiscrepancyStatus
    reason?: string
    _id?: string
  }
  mediaAttachments?: MediaAttachment[]
  metaData?: {
    description?: string
    [key: string]: any
  }
  changelog?: ChangelogEntry[]
  [key: string]: any
}

export type ChartDataPoint = {
  date: string
  stockLevel: number
  recordId?: string
  reason?: string
  expectedStockLevel?: number
  // Marker configuration (already calculated in backend)
  marker?: {
    symbol: string | null
    height?: number
    width?: number
    color: string
    lineColor: string
  }
  // Tooltip data (optional - can be derived from record if needed)
  record?: StockRecord
}

export type StockRecordsResponse = {
  records: StockRecord[]
  chartData: ChartDataPoint[]
  total: number
  suggestedActivityDate?: string
}

export type ResidentMedicationsResponse = {
  scheduledMedications: ResidentMedication[]
  prnMedications: ResidentMedication[]
  discontinuedMedications: ResidentMedication[]
}

export type AddStockData = {
  description: string
  receivedStock: number
  newStock: number
  isStockAccurate: boolean
  receivedBy: string
  actionTime?: any // dayjs object
  resolveDiscrepancy: string | null
  discrepancyReason: string
  receivedFrom: string
  witnessPin: string
  additionalDetails: boolean
  media?: File[]
  mediaAttachmentKeysToRemove?: string[]
}

export type CurrentStockResponse = {
  currentStockLevel: number
  unit: string
  lastStockRecordDate: string
  resident?: {
    id: string
    name: string
    preferredName?: string
    image?: string
  }
  witnesses?: Array<{
    userId: string
    witnessedAt: string
    addedBy: string
    name: string
  }>
  notes?: string | string[]
  disposalMethod?: string
  sourceRecordId?: string
  stockAlertLevel?: number
  stockQuantityType?: string
  deliveryId?: string
  relatedRecordId?: any
  discrepancy?: any
  changelog?: string[]
  mediaAttachments?: Array<{
    url: string
    key: string
    bucket: string
    contentType: string
    size: number
    uploadedAt: string
    uploadedBy: string
  }>
}

export type MedicationTableData = {
  name: string
  currentStock: number
  startDate: string
  endDate: string
  isControlledDrug: boolean
  lowStock?: boolean
  outOfStock?: boolean
  stockUnit?: string
  stockAlertLevel?: number
  isDiscontinued?: boolean
  secondSignatureRequired?: boolean
}

export type MarChartWitness = {
  userId: string
  name?: string
  picture?: string | null
  witnessedAt?: string
}

/**
 * The medication as it was configured when the round was recorded — which can
 * differ from the medication's current configuration, so the UI prefers this
 * snapshot over the live medication when describing a past administration.
 */
export type MarChartAdministrationMedication = {
  doseQuantity?: number
  doseUnit?: string
  altDose?: string | number
  hasStockTracking?: boolean
}

/**
 * Detail the MAR chart nests one level deeper, under
 * `dateStatus.administration.administration`. Where `status` is present it wins
 * over the envelope's `administrationStatus` (see `handleCellClick`).
 */
export type MarChartAdministrationDetail = {
  status?: AdministrationStatus | string
  /** false when stock tracking is off for this medication */
  stockAffected?: boolean
  /** what was recorded as used, when stock is not tracked */
  doseGiven?: number | string
}

/**
 * An administration record as it arrives nested on a MAR chart `dateStatus`.
 *
 * Deliberately *not* `StockRecord['administration']`: the MAR chart endpoint
 * returns this envelope — id, who recorded it, the medication snapshot, the
 * stock movement, and a nested detail object — whereas the stock endpoints
 * return a flattened record. `handleCellClick` in EMARResidentScreenV2 maps
 * this shape onto a `StockRecord` for the drawer.
 *
 * Every field here is read somewhere in the eMAR V2 screens, and test fixtures
 * are checked against it, so this type is the MAR chart contract. If a field is
 * missing, add it *together with* the code that consumes it — don't cast.
 */
export type MarChartAdministration = {
  _id: string
  date?: string
  actionTime?: string
  createdAt?: string
  updatedAt?: string
  administeredTime?: string
  status?: string
  administrationStatus?: AdministrationStatus | string
  recordedBy?: {
    id: string
    name: { first: string; last: string } | string
    picture?: string | null
  }
  administeredBy?: {
    id: string
    name: { first: string; last: string } | string
    picture?: string | null
  }
  dose?: string | number
  doseQuantity?: number
  quantityType?: string
  note?: string | null
  notes?: string | string[]
  witnessed?: boolean
  witnesses?: StockRecordWitness[]
  changelog?: ChangelogEntry[]
  backsignedAt?: string
  reasonForLateSignature?: string
  medication?: MarChartAdministrationMedication
  stock?: {
    quantityChange?: number
    unit?: string
  }
  administration?: MarChartAdministrationDetail
}

export type MarChartDateStatus = {
  date: string
  dayOfWeek: string
  status: string
  symbol?: string
  timingTime?: string
  timingName?: string
  roundTimingId?: string
  administeredTime?: string
  doseQuantity?: number
  doseUnit?: string
  altDose?: string | number
  recordedBy?: {
    id: string
    name: {
      first: string
      last: string
    }
    picture?: string | null
  }
  details?: string
  reasonCode?: string
  administration?: MarChartAdministration
  note?: string
  reasonForLateSignature?: string
  administeredBy?: {
    id: string
    name: { first: string; last: string }
    picture?: string | null
  }
  witnesses?: MarChartWitness[]
  /** Whether the administration moved stock. Absent when stock is untracked. */
  stockChanged?: boolean | string
  /** Set when the round was signed for retrospectively. */
  backsignedAt?: string
}

/** Who recorded an outcome and what they recorded, for the outcome popover. */
export type MarChartOutcomeRecordDetails = {
  quantity: string
  timeRecorded: string
  recordedById: string
  recordedBy: string
  recordedByPicture?: string | null
  comment?: string
  comments: string[]
  witnesses: MarChartWitness[]
}

/**
 * One outcome within a MAR chart cell — a cell holds several when a round was
 * recorded more than once on the same date. Built by `transformMarChartData`.
 */
export type MarChartOutcome = {
  /** Translation key from `getAdministrationStatusDisplay`, or 'SCHEDULED'. */
  action: string
  /** The cell's date, DD-MM-YYYY. */
  date: string
  recordDetails?: MarChartOutcomeRecordDetails
  dateStatus?: MarChartDateStatus
  administration?: MarChartAdministration
  /**
   * Defensive fallback for the outcome popover: `transformMarChartData` puts
   * this on `dateStatus`, not here, but the popover reads it from either. Keep
   * it declared so the fallback isn't deleted as dead code.
   */
  stockChanged?: boolean | string
}

/**
 * The row fields the outcome popover reads. Kept narrow so a spec can build one
 * by hand; a full `MedicationTableRow` satisfies it structurally.
 */
export type MarChartPopoverRecord = {
  medication?: { name?: string }
  dose?: string
  hour?: string
}

/** A single day's cell in a MAR chart row. */
export type MarChartCell = {
  outcomes: MarChartOutcome[]
}

export type MarChartRoundTiming = {
  roundTimingId: string
  timingName: string
  timingTime: string
  dose: string
  customDose?: string
  doseUnit?: string
  dateStatuses: MarChartDateStatus[]
}

export type MarChartMedication = {
  residentMedicationId: string
  patternId: string
  medicationId: string
  medicationName: string
  currentStock: number
  stockUnit?: string
  startDate: string
  endDate: string
  dose: string | object
  doseUnit?: string
  /** Null and '' both mean "no alt dose configured". */
  altDose?: string | number | null
  controlledDrug: boolean
  secondSignatureRequired: boolean
  variableDose: boolean
  blisterPack: boolean
  quantityType?: string
  homeRemedy: boolean
  overTheCounter: boolean
  hasStockTracking: boolean
  prn: boolean
  discontinued?: boolean
  /** Stock level at or below which the row is flagged as low stock. */
  stockAlertLevel?: number
  roundTimings: MarChartRoundTiming[]
}

export type MarChartData = {
  medications: MarChartMedication[]
  dateRange: {
    startDate: string
    endDate: string
  }
}

export type MedicationSchedulePatternDose = {
  dose: number
  customDose: number | null
  altDose: number | null
  unit: string
  roundTimingId: string
}

export type MedicationSchedulePattern = {
  status: string
  version: number
  _id: string
  blisterPack: boolean
  controlledDrug: boolean
  createdBy: string
  createdOn: string
  deleted: boolean
  doses: MedicationSchedulePatternDose[]
  frequency: string
  frequencyError: string | null
  homeId: string
  homeRemedy: boolean
  interval: number
  medicationId: string
  medicationName: string
  overTheCounter: boolean
  prescriptionCount: number
  prn: boolean
  quantityType: string
  residentId: string
  residentMedicationId: string
  route: string
  secondSignatureRequired: boolean
  startDate: string
  stockAlertLevel: number
  stockQuantityType: string
  timezone: string
  variableDose: boolean
  createdAt: string
  updatedAt: string
  discontinued?: {
    at: string
    by: string
    reason: string
  }
  paused?: {
    at: string
    by: string
    reason: string
  }
  endDate?: string
  trackStock?: boolean
}

export type MedicationTableRow = {
  key: string
  medication: MedicationTableData
  hour?: string
  dose: string
  altDose?: string
  residentMedicationId: string
  [dateKey: string]: any
}

export type AdministrationRecord = {
  type: 'administration'
  id: string
  date: string
  actionTime?: string
  administration: {
    _id: string
    createdAt: string
    updatedAt: string
    recordedBy: {
      id: string
      name: {
        first: string
        last: string
      }
      picture?: string
    }
    updatedBy: string
    homeId: string
    status: string
    residentId: string
    patternId: string
    medicationId: string
    residentMedicationId: string
    roundTimingId: string
    startTime: string
    endTime: string
    dose: string
    customDose: string
    doseQuantity: number
    prn: boolean
    administrationStatus: AdministrationStatus | string
    administeredTime: string
    reasonCode?: string | null
    note?: string | null
    deleted: boolean
    emarRoundKey: string
    roundOverdue: boolean
    roundType: string
    resident?: {
      id: string
      name: {
        first: string
        last: string
      }
      firstName?: string
      picture?: string
    }
    createdByUser?: {
      id: string
      name: {
        first: string
        last: string
      }
      email?: string
      picture?: string
      [key: string]: any
    }
    [key: string]: any
  }
}
